import { memo, useCallback, useRef } from 'react';
import cn from 'classnames';
import { format, isToday, isYesterday } from 'date-fns';
import { Avatar, Button, Input, Spinner } from '@heroui/react';
import { CHAT_LIST_SCROLL_THRESHOLD_PX, CHAT_UNREAD_BADGE_CLASS } from '../../config/constants';
import { useNearScrollEnd } from '../../lib/useNearScrollEnd';
import type { ChatThread } from '../../model/types';
import { IconGroup, IconPersonal, IconSearch } from '../icons';

function formatListTime(d: Date | null) {
  if (!d) return '';
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'd MMM');
}

export type ChatSidebarProps = {
  threads: ChatThread[];
  activeId: number | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onSelect: (id: number) => void;
  onMessageUser: () => void;
  onCreateGroup?: () => void;
  canCreateGroup?: boolean;
};

function ThreadKindIcon({ kind }: { kind: ChatThread['kind'] }) {
  return kind === 'group' ? (
    <IconGroup className="size-4 shrink-0 text-default-600" />
  ) : (
    <IconPersonal className="size-4 shrink-0 text-default-600" />
  );
}

export const ChatSidebar = memo(
  ({
    threads,
    activeId,
    searchQuery,
    onSearchChange,
    isLoading = false,
    isLoadingMore = false,
    hasMore = false,
    onLoadMore,
    onSelect,
    onMessageUser,
    onCreateGroup,
    canCreateGroup,
  }: ChatSidebarProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleNearEnd = useCallback(() => {
      onLoadMore?.();
    }, [onLoadMore]);

    useNearScrollEnd(scrollRef, handleNearEnd, {
      thresholdPx: CHAT_LIST_SCROLL_THRESHOLD_PX,
      disabled: !hasMore || isLoading || isLoadingMore,
    });

    const showInitialLoading = isLoading && threads.length === 0;
    const showEmpty = !isLoading && threads.length === 0;

    return (
      <aside
        aria-label="Chats"
        className="flex min-h-0 w-full flex-col md:w-[min(100%,320px)] md:shrink-0"
      >
        <div className="flex shrink-0 gap-2 px-3 pb-2 pt-1">
          <Button
            className={cn(
              'h-11 min-h-11 flex-1 rounded-xl bg-[#27245F] text-[14px] font-medium text-white',
              !canCreateGroup && 'w-full',
            )}
            startContent={<IconPersonal className="text-white" />}
            onPress={onMessageUser}
          >
            Message user
          </Button>
          {canCreateGroup && onCreateGroup ? (
            <Button
              variant="bordered"
              className="h-11 min-h-11 flex-1 rounded-xl border-[#27245F] bg-white text-[14px] font-medium text-[#27245F]"
              startContent={<IconGroup />}
              onPress={onCreateGroup}
            >
              Create group
            </Button>
          ) : null}
        </div>

        <div className="shrink-0 px-3 pb-2">
          <Input
            aria-label="Search chats"
            placeholder="Search chats"
            value={searchQuery}
            onValueChange={onSearchChange}
            startContent={<IconSearch className="text-default-400" />}
            classNames={{
              inputWrapper: 'h-10 min-h-10 rounded-xl bg-default-100 shadow-none',
              input: 'text-small',
            }}
            isClearable
            onClear={() => onSearchChange('')}
          />
        </div>

        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-2 pb-4"
        >
          {showInitialLoading ? (
            <div className="flex justify-center py-10">
              <Spinner size="sm" color="default" />
            </div>
          ) : null}

          {showEmpty ? (
            <p className="px-2 py-8 text-center text-small text-default-500">
              {searchQuery.trim() ? 'No chats match your search' : 'No chats yet'}
            </p>
          ) : null}

          {threads.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {threads.map((thread) => {
                const selected = thread.id === activeId;
                return (
                  <li key={thread.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(thread.id)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-2xl p-3 text-left transition-colors',
                        selected ? 'bg-default-100' : 'bg-content1 hover:bg-default-50/90',
                      )}
                    >
                      <div className="relative shrink-0">
                        <Avatar name={thread.title} src={thread.avatarUrl} size="md" showFallback />
                        {thread.kind === 'personal' && thread.online ? (
                          <span
                            className="absolute right-0 bottom-0 size-2.5 rounded-full border-2 border-white bg-success"
                            aria-hidden
                          />
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <ThreadKindIcon kind={thread.kind} />
                            <span className="truncate text-small font-semibold text-foreground">
                              {thread.title}
                            </span>
                          </div>
                          <span className="shrink-0 pt-0.5 text-[11px] text-default-600 tabular-nums">
                            {formatListTime(thread.updatedAt)}
                          </span>
                        </div>
                        <div className="mt-1 flex items-end justify-between gap-2">
                          <p className="min-w-0 flex-1 line-clamp-2 text-tiny leading-snug text-default-600">
                            {thread.lastMessagePreview}
                          </p>
                          {thread.unreadCount > 0 ? (
                            <span className={CHAT_UNREAD_BADGE_CLASS} aria-label={`${thread.unreadCount} unread`}>
                              {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {isLoadingMore ? (
            <div className="flex justify-center py-4">
              <Spinner size="sm" color="default" />
            </div>
          ) : null}
        </div>
      </aside>
    );
  },
);

ChatSidebar.displayName = 'ChatSidebar';
